import { useMemo } from "react";
import { useViewerDeploymentActions } from "./useViewerDeploymentActions";
import { useViewerProjectState } from "./useViewerProjectState";
import type { ViewerDraftSaveStatus, ViewerJobStatus } from "../types/viewerDeployment";

export interface ViewerAdminDeploymentRuntimeState {
  status: ReturnType<typeof useViewerProjectState>["status"];
  error: string | null;
  previewLabel: "Preview" | "Update";
  isPreviewPending: boolean;
  isDeployPending: boolean;
  canCreatePreview: boolean;
  canDeploy: boolean;
  deploymentError: string | null;
  jobStatus: ViewerJobStatus | null;
}

export interface ViewerAdminDeploymentRuntimeActions {
  createPreview: ReturnType<typeof useViewerDeploymentActions>["createPreview"];
  deploy: ReturnType<typeof useViewerDeploymentActions>["deploy"];
}

// Shared empty state lets the viewer shell stay stable before the
// deployment runtime has mounted or when admin deployment controls are disabled.
export const EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE: ViewerAdminDeploymentRuntimeState =
  {
  status: "idle",
  error: null,
  previewLabel: "Preview",
  isPreviewPending: false,
  isDeployPending: false,
  canCreatePreview: false,
  canDeploy: false,
  deploymentError: null,
  jobStatus: null,
  };

export const EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS: ViewerAdminDeploymentRuntimeActions =
  {
  createPreview: async () => null,
  deploy: async () => null,
  };

interface UseViewerAdminDeploymentRuntimeParams {
  enabled: boolean;
  companyId: string | null;
  productId: string | null;
  expectedDraftRevision?: number | null;
  saveStatus?: ViewerDraftSaveStatus;
}

export function useViewerAdminDeploymentRuntime(
  params: UseViewerAdminDeploymentRuntimeParams,
) {
  const viewerProjectState = useViewerProjectState({
    enabled: params.enabled,
    companyId: params.companyId,
    productId: params.productId,
  });
  const viewerDeploymentActions = useViewerDeploymentActions({
    enabled: params.enabled,
    companyId: params.companyId,
    productId: params.productId,
    projectStatus: viewerProjectState.projectStatus,
    expectedDraftRevision: params.expectedDraftRevision ?? null,
    saveStatus: params.saveStatus ?? "idle",
    onProjectStatusChanged: viewerProjectState.refetch,
  });

  return useMemo(
    () => ({
      state: params.enabled
        ? {
            status: viewerProjectState.status,
            error: viewerProjectState.error,
            previewLabel: viewerDeploymentActions.previewLabel,
            isPreviewPending: viewerDeploymentActions.isPreviewPending,
            isDeployPending: viewerDeploymentActions.isDeployPending,
            canCreatePreview: viewerDeploymentActions.canCreatePreview,
            canDeploy: viewerDeploymentActions.canDeploy,
            deploymentError: viewerDeploymentActions.deploymentError,
            jobStatus: viewerDeploymentActions.jobStatus,
          }
        : EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE,
      actions: params.enabled
        ? {
            createPreview: viewerDeploymentActions.createPreview,
            deploy: viewerDeploymentActions.deploy,
          }
        : EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS,
    }),
    [
      params.enabled,
      viewerDeploymentActions.canCreatePreview,
      viewerDeploymentActions.canDeploy,
      viewerDeploymentActions.createPreview,
      viewerDeploymentActions.deploy,
      viewerDeploymentActions.deploymentError,
      viewerDeploymentActions.isDeployPending,
      viewerDeploymentActions.isPreviewPending,
      viewerDeploymentActions.jobStatus,
      viewerDeploymentActions.previewLabel,
      viewerProjectState.error,
      viewerProjectState.status,
    ],
  );
}
